import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Search, Copy, Check, ChevronRight, Filter, BookOpen, Pill, Award } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  nota: { label: 'Note / Consigli', color: '#2A9FCB', bg: '#EFF6FF', icon: FileText },
  terapia: { label: 'Terapie', color: '#059669', bg: '#ECFDF5', icon: Pill },
  prescrizione: { label: 'Prescrizioni / Certificati', color: '#D97706', bg: '#FFFBEB', icon: Award },
};

export default function ClinicalTemplates() {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchTemplates();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/clinical-templates/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(res.data.categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchTemplates = async (category, type) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (type) params.append('type', type);
      const res = await axios.get(`${API}/api/clinical-templates?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectCategory = (catName) => {
    if (selectedCategory === catName) {
      setSelectedCategory(null);
      setSelectedTemplate(null);
      fetchTemplates(null, selectedType);
    } else {
      setSelectedCategory(catName);
      setSelectedTemplate(null);
      fetchTemplates(catName, selectedType);
    }
  };

  const selectType = (type) => {
    const newType = selectedType === type ? null : type;
    setSelectedType(newType);
    setSelectedTemplate(null);
    fetchTemplates(selectedCategory, newType);
  };

  const copyToClipboard = async (template) => {
    try {
      await navigator.clipboard.writeText(template.content);
      setCopiedId(template.id);
      toast.success('Template copiato negli appunti');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Errore nella copia');
    }
  };

  const filteredTemplates = templates.filter(t =>
    !searchQuery ||
    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.subcategory.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="clinical-templates-tab">
      {/* Header */}
      <div className="rounded-2xl p-6 shadow-sm" style={{ background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)' }}>
        <h2 className="text-2xl font-semibold mb-1 flex items-center gap-2" style={{ color: '#2A9FCB' }}>
          <BookOpen className="w-6 h-6" />
          Template Clinici
        </h2>
        <p className="text-[#2A9FCB]/70 text-sm">
          Consulta e copia i template per note cliniche, terapie e prescrizioni.
        </p>
        <div className="flex gap-2 mt-3">
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <button
                key={key}
                onClick={() => selectType(key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                  selectedType === key ? 'ring-2 ring-offset-2 scale-105 shadow-sm' : 'opacity-80 hover:opacity-100'
                }`}
                style={{ background: selectedType === key ? cfg.color : cfg.bg, color: selectedType === key ? 'white' : cfg.color }}
                data-testid={`type-filter-${key}`}
              >
                <Icon className="w-4 h-4" /> {cfg.label}
              </button>
            );
          })}
          {selectedType && (
            <button
              onClick={() => selectType(null)}
              className="text-xs text-gray-400 hover:text-gray-600 px-2"
            >
              Mostra tutti
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Sidebar - Categories */}
        <div className="lg:w-64 shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="template-categories">
            <div className="p-3 border-b border-gray-100">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Categorie</p>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {categories.map(cat => (
                <button
                  key={cat.name}
                  onClick={() => selectCategory(cat.name)}
                  data-testid={`cat-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`w-full text-left px-4 py-3 flex items-center justify-between border-b border-gray-50 transition-all text-sm ${
                    selectedCategory === cat.name
                      ? 'bg-[#2A9FCB]/10 text-[#2A9FCB] font-semibold'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="truncate">{cat.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    selectedCategory === cat.name ? 'bg-[#2A9FCB] text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {cat.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Cerca template per titolo, contenuto o sottocategoria..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]/30 focus:border-[#2A9FCB]"
              data-testid="template-search"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <div className="animate-spin w-6 h-6 border-2 border-[#2A9FCB] border-t-transparent rounded-full" />
            </div>
          ) : selectedTemplate ? (
            /* Template Detail View */
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="template-detail">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="text-sm text-[#2A9FCB] hover:underline flex items-center gap-1"
                  data-testid="template-back"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" /> Torna alla lista
                </button>
                <button
                  onClick={() => copyToClipboard(selectedTemplate)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    copiedId === selectedTemplate.id
                      ? 'bg-green-100 text-green-700'
                      : 'bg-[#2A9FCB] text-white hover:bg-[#2590b8]'
                  }`}
                  data-testid="template-copy-btn"
                >
                  {copiedId === selectedTemplate.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copiedId === selectedTemplate.id ? 'Copiato!' : 'Copia negli Appunti'}
                </button>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  {(() => {
                    const cfg = TYPE_CONFIG[selectedTemplate.type] || TYPE_CONFIG.nota;
                    const Icon = cfg.icon;
                    return (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: cfg.bg, color: cfg.color }}>
                        <Icon className="w-3.5 h-3.5" /> {cfg.label}
                      </span>
                    );
                  })()}
                  <span className="text-xs text-gray-400">{selectedTemplate.category} &rsaquo; {selectedTemplate.subcategory}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{selectedTemplate.title}</h3>
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed font-sans">
                    {selectedTemplate.content}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            /* Template List */
            <div className="space-y-2" data-testid="template-list">
              <p className="text-xs text-gray-400 mb-2">
                {filteredTemplates.length} template{filteredTemplates.length !== 1 && 'i'}
                {selectedCategory && ` in "${selectedCategory}"`}
              </p>
              {filteredTemplates.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Nessun template trovato</p>
                </div>
              ) : (
                filteredTemplates.map(template => {
                  const cfg = TYPE_CONFIG[template.type] || TYPE_CONFIG.nota;
                  const Icon = cfg.icon;
                  return (
                    <div
                      key={template.id}
                      className="bg-white rounded-xl border border-gray-200 p-4 hover:border-[#2A9FCB]/40 hover:shadow-sm transition-all cursor-pointer group"
                      onClick={() => setSelectedTemplate(template)}
                      data-testid={`template-item-${template.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color }}>
                              <Icon className="w-3 h-3" /> {cfg.label}
                            </span>
                            <span className="text-xs text-gray-400">{template.subcategory}</span>
                          </div>
                          <h4 className="font-semibold text-gray-900 text-sm group-hover:text-[#2A9FCB] transition-colors">
                            {template.title}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {template.content.substring(0, 120)}...
                          </p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(template); }}
                          className={`shrink-0 p-2 rounded-lg transition-all ${
                            copiedId === template.id
                              ? 'bg-green-100 text-green-600'
                              : 'text-gray-300 hover:text-[#2A9FCB] hover:bg-[#2A9FCB]/10'
                          }`}
                          title="Copia negli appunti"
                          data-testid={`copy-${template.id}`}
                        >
                          {copiedId === template.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
